import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  LinearProgress,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  People as PeopleIcon,
  Store as StoreIcon,
  ReceiptLong as ReceiptLongIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  TrendingUp,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  ArrowForward,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import {
  fetchDashboardStats,
  fetchMerchants,
  fetchTransactionVolume,
  fetchUserGrowth,
  fetchRecentTransactions,
} from '../../store/slices/adminSlice';
import { useNavigate } from 'react-router-dom';

interface StatCard {
  title: string;
  value: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  change: number;
  period: string;
}

const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const {
    admin, dashboardStats, statsLoading, merchants,
    transactionVolume, userGrowth, recentTransactions,
  } = useSelector((state: RootState) => state.admin);

  const [currentTime] = useState(new Date());

  // Fetch all data on mount
  useEffect(() => {
    dispatch(fetchDashboardStats());
    dispatch(fetchMerchants({ limit: 10, status: 'pending' }));
    dispatch(fetchTransactionVolume());
    dispatch(fetchUserGrowth());
    dispatch(fetchRecentTransactions(5));
  }, [dispatch]);

  // Format currency
  const formatCurrency = (value: number) => {
    return value.toLocaleString('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    });
  };

  // Get stat cards with actual data or defaults
  const statCards: StatCard[] = [
    {
      title: 'Total Users',
      value: (dashboardStats?.totalUsers ?? 0).toLocaleString(),
      icon: (
        <PeopleIcon
          sx={{ fontSize: 32, color: 'white' }}
        />
      ),
      color: '#11C1EC',
      bgColor: '#11C1EC',
      change: 12.5,
      period: 'vs last month',
    },
    {
      title: 'Total Merchants',
      value: (dashboardStats?.totalMerchants ?? 0).toLocaleString(),
      icon: (
        <StoreIcon
          sx={{ fontSize: 32, color: 'white' }}
        />
      ),
      color: '#00C48C',
      bgColor: '#00C48C',
      change: 8.2,
      period: 'vs last month',
    },
    {
      title: 'Total Transactions',
      value: (dashboardStats?.totalTransactions ?? 0).toLocaleString(),
      icon: (
        <ReceiptLongIcon
          sx={{ fontSize: 32, color: 'white' }}
        />
      ),
      color: '#FFB946',
      bgColor: '#FFB946',
      change: 15.3,
      period: 'vs last month',
    },
    {
      title: 'Total Revenue',
      value: formatCurrency(dashboardStats?.totalRevenue || 0),
      icon: (
        <AccountBalanceWalletIcon
          sx={{ fontSize: 32, color: 'white' }}
        />
      ),
      color: '#09486D',
      bgColor: '#09486D',
      change: 6.8,
      period: 'vs last month',
    },
  ];

  // Get pending merchants for approval
  const pendingMerchants = merchants.filter((m) => m.status === 'pending').slice(0, 5);

  return (
    <Box sx={{ maxWidth: { xs: '100%', md: 1400 }, mx: 'auto', px: { xs: 2, md: 3 } }}>
      {/* Welcome Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ color: '#7F839C', mb: 0.5, fontSize: '0.9rem' }}>
          Welcome back,
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: '#202647',
              fontSize: { xs: '1.5rem', sm: '2.125rem' },
            }}
          >
            {admin?.name || 'Admin'}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: '#7F839C', fontSize: '0.9rem', fontWeight: 500 }}
          >
            {currentTime.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </Typography>
        </Box>
      </Box>

      {/* Stats Cards Row */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {statCards.map((stat, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <Card
              sx={{
                borderRadius: 4,
                border: 'none',
                height: '100%',
                background: `linear-gradient(135deg, ${stat.bgColor}15 0%, ${stat.bgColor}08 100%)`,
                backdropFilter: 'blur(12px)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 32px ${stat.bgColor}20`,
                },
              }}
            >
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Icon Background */}
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}dd 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    boxShadow: `0 4px 15px ${stat.color}30`,
                  }}
                >
                  {stat.icon}
                </Box>

                {/* Title */}
                <Typography
                  variant="body2"
                  sx={{
                    color: '#7F839C',
                    fontWeight: 500,
                    mb: 1,
                    fontSize: '0.875rem',
                  }}
                >
                  {stat.title}
                </Typography>

                {/* Value */}
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#202647',
                    mb: 1.5,
                    fontSize: '1.75rem',
                  }}
                >
                  {statsLoading ? (
                    <CircularProgress size={24} sx={{ color: stat.color }} />
                  ) : (
                    stat.value
                  )}
                </Typography>

                {/* Change Indicator */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TrendingUp sx={{ fontSize: 16, color: '#00C48C' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#00C48C',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                    }}
                  >
                    +{stat.change}%
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#7F839C',
                      fontSize: '0.8rem',
                    }}
                  >
                    {stat.period}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Transaction Volume Chart */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 4, border: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#202647' }}>
                  Transaction Volume
                </Typography>
                <Chip
                  label="Last 7 days"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(17, 193, 236, 0.08)',
                    color: '#09486D',
                    fontWeight: 600,
                  }}
                />
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={transactionVolume.length > 0 ? transactionVolume : [{ name: 'No data', transactions: 0, volume: 0 }]}>
                  <defs>
                    <linearGradient id="colorVolume" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#11C1EC" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#11C1EC" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E8F0" />
                  <XAxis dataKey="name" stroke="#7F839C" />
                  <YAxis stroke="#7F839C" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: `1px solid #E8E8F0`,
                      borderRadius: 8,
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="transactions"
                    stroke="#11C1EC"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorVolume)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* User Growth Chart */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 4, border: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#202647' }}>
                  User Growth
                </Typography>
                <Chip
                  label="Last 6 months"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(0, 196, 140, 0.08)',
                    color: '#009A6E',
                    fontWeight: 600,
                  }}
                />
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={userGrowth.length > 0 ? userGrowth : [{ name: 'No data', users: 0 }]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E8F0" />
                  <XAxis dataKey="name" stroke="#7F839C" />
                  <YAxis stroke="#7F839C" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: `1px solid #E8E8F0`,
                      borderRadius: 8,
                    }}
                  />
                  <Bar dataKey="users" fill="#11C1EC" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Activity Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Recent Transactions */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 4, border: 'none' }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#202647' }}>
                    Recent Transactions
                  </Typography>
                  <Chip
                    label="View all"
                    size="small"
                    onClick={() => navigate('/admin/earnings')}
                    icon={<ArrowForward sx={{ fontSize: 14 }} />}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: 'rgba(17, 193, 236, 0.08)',
                      color: '#09486D',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      '& .MuiChip-icon': {
                        color: '#11C1EC',
                        order: 1,
                        ml: 0.5,
                        mr: -0.5,
                      },
                      '&:hover': { bgcolor: 'rgba(17, 193, 236, 0.15)' },
                    }}
                  />
                </Box>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#F8F8FC' }}>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: '#7F839C',
                          borderColor: '#E8E8F0',
                          fontSize: '0.875rem',
                        }}
                      >
                        User
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 600,
                          color: '#7F839C',
                          borderColor: '#E8E8F0',
                          fontSize: '0.875rem',
                        }}
                      >
                        Amount
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontWeight: 600,
                          color: '#7F839C',
                          borderColor: '#E8E8F0',
                          fontSize: '0.875rem',
                        }}
                      >
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(recentTransactions.length > 0 ? recentTransactions : []).map((tx) => (
                      <TableRow
                        key={tx.id}
                        sx={{
                          '&:hover': { backgroundColor: 'rgba(17, 193, 236, 0.03)' },
                          borderColor: '#E8E8F0',
                        }}
                      >
                        <TableCell
                          sx={{
                            borderColor: '#E8E8F0',
                            color: '#272A3A',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                          }}
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <span>{tx.user}</span>
                            <span
                              style={{
                                fontSize: '0.8rem',
                                color: '#7F839C',
                                marginTop: 2,
                              }}
                            >
                              {tx.date ? new Date(tx.date).toLocaleDateString() : ''}
                            </span>
                          </Box>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            borderColor: '#E8E8F0',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: tx.type === 'transfer' ? '#FF4757' : '#00C48C',
                              fontWeight: 600,
                            }}
                          >
                            {tx.type === 'transfer' ? '-' : '+'}
                            {formatCurrency(tx.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ borderColor: '#E8E8F0' }}>
                          <Chip
                            label={tx.status}
                            size="small"
                            icon={
                              tx.status === 'completed' ? (
                                <CheckCircleIcon sx={{ fontSize: 14 }} />
                              ) : tx.status === 'failed' ? (
                                <ErrorIcon sx={{ fontSize: 14 }} />
                              ) : (
                                <ScheduleIcon sx={{ fontSize: 14 }} />
                              )
                            }
                            sx={{
                              height: 24,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              textTransform: 'capitalize',
                              bgcolor:
                                tx.status === 'completed'
                                  ? 'rgba(0, 196, 140, 0.1)'
                                  : tx.status === 'failed'
                                    ? 'rgba(255, 71, 87, 0.1)'
                                    : 'rgba(255, 185, 70, 0.1)',
                              color:
                                tx.status === 'completed'
                                  ? '#00C48C'
                                  : tx.status === 'failed'
                                    ? '#FF4757'
                                    : '#FFB946',
                              '& .MuiChip-icon': {
                                fontSize: 14,
                                marginLeft: '-4px !important',
                              },
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Pending Merchant Approvals */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 4, border: 'none' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#202647' }}>
                  Pending Merchant Approvals
                </Typography>
                <Chip
                  label={`${dashboardStats?.pendingMerchants || 0} pending`}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255, 185, 70, 0.1)',
                    color: '#FFB946',
                    fontWeight: 600,
                  }}
                />
              </Box>

              {pendingMerchants.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No pending merchant approvals at the moment.
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  {pendingMerchants.map((merchant) => (
                    <Box
                      key={merchant.id}
                      sx={{
                        p: 2,
                        backgroundColor: '#F8F8FC',
                        borderRadius: 2,
                        border: '1px solid #E8E8F0',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: '#F0F0F6',
                          borderColor: '#11C1EC',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: '#202647',
                              fontSize: '0.95rem',
                            }}
                          >
                            {merchant.businessName}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#7F839C',
                              fontSize: '0.85rem',
                              mt: 0.5,
                            }}
                          >
                            {merchant.email}
                          </Typography>
                        </Box>
                        <Chip
                          label={merchant.status}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(255, 185, 70, 0.1)',
                            color: '#FFB946',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            textTransform: 'capitalize',
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => navigate('/admin/merchants')}
                          sx={{
                            background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            flex: 1,
                          }}
                        >
                          Review
                        </Button>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              <Button
                fullWidth
                onClick={() => navigate('/admin/merchants')}
                sx={{
                  mt: 3,
                  color: '#11C1EC',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderBottom: '2px solid #11C1EC',
                  borderRadius: 0,
                  '&:hover': { backgroundColor: 'rgba(17, 193, 236, 0.05)' },
                }}
              >
                View all merchants
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Health Section */}
      <Card sx={{ borderRadius: 4, border: 'none' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#202647', mb: 3 }}>
            System Health
          </Typography>

          <Grid container spacing={3}>
            {/* Server Status */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: '#00C48C',
                      boxShadow: '0 0 8px rgba(0, 196, 140, 0.5)',
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.7 },
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#7F839C' }}>
                    Server Status
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#00C48C' }}>
                  Operational
                </Typography>
              </Box>
            </Grid>

            {/* Database Status */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: '#00C48C',
                      boxShadow: '0 0 8px rgba(0, 196, 140, 0.5)',
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.7 },
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#7F839C' }}>
                    Database Status
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#00C48C' }}>
                  Healthy
                </Typography>
              </Box>
            </Grid>

            {/* API Response Time */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#7F839C', mb: 1 }}>
                  API Response Time
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#11C1EC' }}>
                    145ms
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#00C48C', fontWeight: 600 }}>
                    Good
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={70}
                  sx={{
                    mt: 1,
                    backgroundColor: 'rgba(17, 193, 236, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#11C1EC',
                    },
                  }}
                />
              </Box>
            </Grid>

            {/* Error Count */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#7F839C', mb: 1 }}>
                  Errors (24h)
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#FF4757' }}>
                  2
                </Typography>
                <Typography variant="body2" sx={{ color: '#7F839C', fontSize: '0.8rem', mt: 0.5 }}>
                  Last error 3h ago
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminDashboardPage;
